import { EventTypes } from 'redux-segment';

import CONF from 'config/conf.singleton';
import H from 'constants/httpMethod.const';
import { AUTH_API } from 'middleware/api';
import authenticatedEndpoint from 'utils/backend/authenticatedEndpoint.util';
import jsonEndpoint from 'utils/backend/jsonEndpoint.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromAnyToUglyJson } from 'utils/converters/json.util';
import { fromAnyDateToZuluDayBgn, fromAnyDateToZuluDayEnd } from 'utils/converters/time/from.util';
import { modifySearchParams } from 'utils/links/modify.util';

import {
  ADD_CLIENT,
  ADD_CLIENT_DEPOSITS,
  ADD_CLIENT_SEARCH_DEPOSITS,
  ADD_PRODUCT,
  ADD_SERVICE,
  APPOINTMENT_FAILURE,
  APPOINTMENT_REQUEST,
  APPOINTMENT_SUCCESS,
  CANCEL_SLICK_CARD_ONLINE,
  CHANGE_ADDITIONAL_DISCOUNT_TYPE,
  CHANGE_PRODUCT_DISCOUNT_TYPE,
  CHANGE_SERVICE_DISCOUNT_TYPE,
  CHANGE_TIP_TYPE,
  CHECKOUT_CLIENT_FAILURE,
  CHECKOUT_CLIENT_REQUEST,
  CHECKOUT_CLIENT_SUCCESS,
  CHECKOUT_CLOSE_SESSION_FAILURE,
  CHECKOUT_CLOSE_SESSION_REQUEST,
  CHECKOUT_CLOSE_SESSION_SUCCESS,
  CHECKOUT_FETCH_ALL_DEPOSITS_FAILURE,
  CHECKOUT_FETCH_ALL_DEPOSITS_REQUEST,
  CHECKOUT_FETCH_ALL_DEPOSITS_SUCCESS,
  CHECKOUT_FETCH_DEPOSIT_FAILURE,
  CHECKOUT_FETCH_DEPOSIT_REQUEST,
  CHECKOUT_FETCH_DEPOSIT_SUCCESS,
  CHECKOUT_INIT_DEPOSIT_FAILURE,
  CHECKOUT_INIT_DEPOSIT_REQUEST,
  CHECKOUT_INIT_DEPOSIT_SUCCESS,
  CHECKOUT_START_SESSION_REQUEST,
  CHECKOUT_START_SESSION_SUCCESS,
  CLEAN_TRANSACTION_DATA,
  CLEAR_TRANSACTION_LOG,
  DEPOSIT_CLIENT_LIST_FAILURE,
  DEPOSIT_CLIENT_LIST_REQUEST,
  DEPOSIT_CLIENT_LIST_SUCCESS,
  EXPORT_TRANSACTION_FAILURE,
  EXPORT_TRANSACTION_REQUEST,
  EXPORT_TRANSACTION_SUCCESS,
  FETCH_NEXT_APPOINTMENT_FAILURE,
  FETCH_NEXT_APPOINTMENT_REQUEST,
  FETCH_NEXT_APPOINTMENT_SUCCESS,
  PRODUCT_CREATE_FAILURE,
  PRODUCT_CREATE_REQUEST,
  PRODUCT_CREATE_SUCCESS,
  PRODUCT_LIST_FAILURE,
  PRODUCT_LIST_REQUEST,
  PRODUCT_LIST_SUCCESS,
  REMOVE_CLIENT,
  REMOVE_PRODUCT,
  REMOVE_SERVICE,
  SET_CHECKOUT_DATA,
  SET_CHECKOUT_INIT_DATA_FROM_CASHUP,
  SET_CHECKOUT_INIT_DATA_FROM_MAIN_NAV,
  SET_CHECKOUT_TOTAL,
  TOGGLE_CHECKOUT_BUTTONS_ENABLED,
  TRANSACTION_DETAILS_FAILURE,
  TRANSACTION_DETAILS_REQUEST,
  TRANSACTION_DETAILS_SUCCESS,
  TRANSACTION_EMAIL_FAILURE,
  TRANSACTION_EMAIL_REQUEST,
  TRANSACTION_EMAIL_SUCCESS,
  UNLOCK_DEPOSIT_FAILURE,
  UNLOCK_DEPOSIT_REQUEST,
  UNLOCK_DEPOSIT_SUCCESS,
  UPDATE_ADDITIONAL_DISCOUNT,
  UPDATE_ADDITIONAL_DISCOUNT_REASON,
  UPDATE_CHECKOUT_COMPLETE,
  UPDATE_DEPOSIT,
  UPDATE_DEPOSIT_FROM_CHECKOUT,
  UPDATE_DEPOSITS,
  UPDATE_OTHER_AMOUNT,
  UPDATE_PART_CASH,
  UPDATE_PRODUCT_DISCOUNT,
  UPDATE_PRODUCT_PRICE,
  UPDATE_PRODUCT_QUANTITY,
  UPDATE_PRODUCT_STYLIST_ID,
  UPDATE_PRODUCT_TITLE,
  UPDATE_SERVICE_DISCOUNT,
  UPDATE_SERVICE_PRICE,
  UPDATE_SERVICE_STYLIST_ID,
  UPDATE_TIP_AMOUNT,
  UPDATE_VOUCHERS_USED,
  USE_DEPOSIT_FAILURE,
  USE_DEPOSIT_REQUEST,
  USE_DEPOSIT_SUCCESS,
} from 'checkout/constants';

export const getAppointment = (salonId, appointmentId) => ({
  [AUTH_API]: {
    ...authenticatedEndpoint(),
    ...jsonEndpoint(H.get, `salon/${salonId}/appointment/${appointmentId}/`),
    params: { id: appointmentId, appointmentId },
    types: [APPOINTMENT_REQUEST, APPOINTMENT_SUCCESS, APPOINTMENT_FAILURE],
  },
});

export const getTransactionDetails = (salonId, transactionId) => ({
  [AUTH_API]: {
    api_root: CONF.transactionApiRoot,
    endpoint: `checkout/retrieve/${salonId}/transaction/${transactionId}`,
    authenticated: true,
    types: [TRANSACTION_DETAILS_REQUEST, TRANSACTION_DETAILS_SUCCESS, TRANSACTION_DETAILS_FAILURE],
  },
});

export const setCheckoutData = (data) => ({ type: SET_CHECKOUT_DATA, data, meta: { analytics: EventTypes.track } });

export function setCheckoutDataFromCashup(data, salonId, transactionId, clients) {
  return {
    type: SET_CHECKOUT_INIT_DATA_FROM_CASHUP,
    data,
    salonId,
    create_from: transactionId,
    finishUrl: `/salon/${salonId}/cashup`,
    clients,
  };
}

export const fetchClientDepositList = (salonId, clientId, appointmentId) => ({
  [AUTH_API]: {
    api_root: CONF.transactionApiRoot,
    endpoint: `deposit/${salonId}/client/${clientId}/available/${modifySearchParams({
      addParams: fromAnyToString(appointmentId)
        .split(',')
        .sort()
        .map(($) => ['for_appointment', $]),
    })}`,
    authenticated: true,
    types: [DEPOSIT_CLIENT_LIST_REQUEST, DEPOSIT_CLIENT_LIST_SUCCESS, DEPOSIT_CLIENT_LIST_FAILURE],
    params: { salonId, clientId, singleApp: !fromAnyToString(appointmentId).includes(',') },
    config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  },
});

export function unlockDeposit(salonId, appointmentId) {
  return {
    [AUTH_API]: {
      api_root: CONF.transactionApiRoot,
      endpoint: `deposit/${salonId}/appointment/${appointmentId}/unlock/`,
      authenticated: true,
      types: [UNLOCK_DEPOSIT_REQUEST, UNLOCK_DEPOSIT_SUCCESS, UNLOCK_DEPOSIT_FAILURE],
      params: { appointmentId },
      config: {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export function setCheckoutInitDataFromCashup(salonId, transactionId, clients, clientId, appointmentId) {
  return (dispatch) => {
    dispatch(fetchClientDepositList(salonId, clientId, appointmentId.toString())).then(() => {
      // eslint-disable-next-line promise/no-nesting
      dispatch(getTransactionDetails(salonId, transactionId)).then((res) => {
        if (res.type === TRANSACTION_DETAILS_SUCCESS) {
          const data = res.response;
          data.created_from = transactionId;
          dispatch(setCheckoutDataFromCashup(data, salonId, transactionId, clients));
        }
        return { type: TRANSACTION_DETAILS_FAILURE };
      });
    });
  };
}

export function setCheckoutInitDataFromNav1(salonId) {
  return {
    type: SET_CHECKOUT_INIT_DATA_FROM_MAIN_NAV,
    salonId,
  };
}

export function setCheckoutInitDataFromNav(salonId) {
  return (dispatch) => {
    dispatch(setCheckoutInitDataFromNav1(salonId));
  };
}

export function setCheckoutTotal(total) {
  return {
    type: SET_CHECKOUT_TOTAL,
    total,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function addClient(client, allProceduresForTheDay) {
  return {
    type: ADD_CLIENT,
    client,
    allProceduresForTheDay,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function addClientDeposits(client, deposit) {
  return {
    type: ADD_CLIENT_DEPOSITS,
    client,
    deposit,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function addClientSearchDeposits(client) {
  return {
    type: ADD_CLIENT_SEARCH_DEPOSITS,
    client,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function removeClient(index) {
  return {
    type: REMOVE_CLIENT,
    index,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function addService(clientIndex, service) {
  return (dispatch, getState) => {
    const {
      currentSalon: { stylistList: stylists, services },
    } = getState();
    dispatch({
      type: ADD_SERVICE,
      clientIndex,
      service,
      stylists,
      services,
      meta: {
        analytics: EventTypes.track,
      },
    });
  };
}

export function removeService(clientIndex, serviceIndex, appointmentId) {
  return {
    type: REMOVE_SERVICE,
    clientIndex,
    serviceIndex,
    appointmentId,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateServiceStylistId(clientIndex, serviceIndex, stylistId) {
  return (dispatch, getState) => {
    const {
      currentSalon: { stylistList: stylists, services },
    } = getState();
    dispatch({
      type: UPDATE_SERVICE_STYLIST_ID,
      clientIndex,
      serviceIndex,
      stylistId,
      stylists,
      categoryServices: services,
      meta: {
        analytics: EventTypes.track,
      },
    });
  };
}

export function updateServicePrice(clientIndex, serviceIndex, price, onMount) {
  return {
    type: UPDATE_SERVICE_PRICE,
    clientIndex,
    serviceIndex,
    price,
    onMount,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateServiceDiscount(clientIndex, serviceIndex, discount) {
  return {
    type: UPDATE_SERVICE_DISCOUNT,
    clientIndex,
    serviceIndex,
    discount,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function changeServiceDiscountType(clientIndex, serviceIndex, discountType) {
  return {
    type: CHANGE_SERVICE_DISCOUNT_TYPE,
    clientIndex,
    serviceIndex,
    discountType,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function addProduct(clientIndex, product) {
  return {
    type: ADD_PRODUCT,
    clientIndex,
    product,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function removeProduct(clientIndex, productIndex) {
  return {
    type: REMOVE_PRODUCT,
    clientIndex,
    productIndex,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateProductStylistId(clientIndex, productIndex, stylistId) {
  return {
    type: UPDATE_PRODUCT_STYLIST_ID,
    clientIndex,
    productIndex,
    stylistId,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateProductTitle(clientIndex, productIndex, title) {
  return {
    type: UPDATE_PRODUCT_TITLE,
    clientIndex,
    productIndex,
    title,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateProductPrice(clientIndex, productIndex, price) {
  return {
    type: UPDATE_PRODUCT_PRICE,
    clientIndex,
    productIndex,
    price,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateProductDiscount(clientIndex, productIndex, discount) {
  return {
    type: UPDATE_PRODUCT_DISCOUNT,
    clientIndex,
    productIndex,
    discount,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateProductQuantity(clientIndex, productIndex, quantity) {
  return {
    type: UPDATE_PRODUCT_QUANTITY,
    clientIndex,
    productIndex,
    quantity,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function changeProductDiscountType(clientIndex, productIndex, discountType) {
  return {
    type: CHANGE_PRODUCT_DISCOUNT_TYPE,
    clientIndex,
    productIndex,
    discountType,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateTipAmount(tipAmount) {
  return {
    type: UPDATE_TIP_AMOUNT,
    tipAmount,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function changeTipType(tipType) {
  return {
    type: CHANGE_TIP_TYPE,
    tipType,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateAdditionalDiscount(additionalDiscount) {
  return {
    type: UPDATE_ADDITIONAL_DISCOUNT,
    additionalDiscount,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function changeAdditionalDiscountType(additionalDiscountType) {
  return {
    type: CHANGE_ADDITIONAL_DISCOUNT_TYPE,
    additionalDiscountType,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateAdditionalDiscountReason(additionalDiscountReason) {
  return {
    type: UPDATE_ADDITIONAL_DISCOUNT_REASON,
    additionalDiscountReason,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updatePartCash(partCash) {
  return {
    type: UPDATE_PART_CASH,
    partCash,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateDeposits(deposits) {
  return {
    type: UPDATE_DEPOSITS,
    deposits,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateDeposit(deposit) {
  return {
    type: UPDATE_DEPOSIT,
    deposit,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function updateDepositFromCheckout(deposit) {
  return {
    type: UPDATE_DEPOSIT_FROM_CHECKOUT,
    response: deposit,
  };
}

export function updateOtherAmount(otherAmount, voucherId) {
  return {
    type: UPDATE_OTHER_AMOUNT,
    otherAmount,
    voucherId,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

// DEPRECATED: most likely unused already in the one place it is imported
export const useDeposit = (salonId, clientId, amount, usedFor) => ({
  ATAG: useDeposit.name,
  [AUTH_API]: {
    api_root: CONF.transactionApiRoot,
    endpoint: `deposit/${salonId}/create/`,
    authenticated: true,
    types: [USE_DEPOSIT_REQUEST, USE_DEPOSIT_SUCCESS, USE_DEPOSIT_FAILURE],
    config: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: fromAnyToUglyJson({
        clientId,
        amount,
        depositType: 'used',
        notes: 'Used in checkout',
        usedFor,
      }),
    },
  },
});

export function cleanTransactionData() {
  return {
    type: CLEAN_TRANSACTION_DATA,
    meta: {
      analytics: EventTypes.track,
    },
  };
}

export function sendEmail(transactionId, to, subject, html, text) {
  return {
    [AUTH_API]: {
      api_root: CONF.transactionApiRoot,
      endpoint: `receipt/email/${transactionId}/`,
      authenticated: true,
      config: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to, subject, html, text }),
      },
      types: [TRANSACTION_EMAIL_REQUEST, TRANSACTION_EMAIL_SUCCESS, TRANSACTION_EMAIL_FAILURE],
    },
  };
}

export function fetchProductList(salonId) {
  return {
    [AUTH_API]: {
      api_root: CONF.stockApiRoot,
      endpoint: `product/quick/${salonId}/`,
      authenticated: true,
      config: {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      types: [PRODUCT_LIST_REQUEST, PRODUCT_LIST_SUCCESS, PRODUCT_LIST_FAILURE],
    },
  };
}

export function createProduct(salonId, title, price) {
  return {
    [AUTH_API]: {
      api_root: CONF.stockApiRoot,
      endpoint: `product/quick/${salonId}/`,
      authenticated: true,
      config: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title, price }),
      },
      types: [PRODUCT_CREATE_REQUEST, PRODUCT_CREATE_SUCCESS, PRODUCT_CREATE_FAILURE],
    },
  };
}

export function fetchClient(salonId, data, method) {
  let url = '';
  if (data.id) {
    url = `salon/${salonId}/client/${data.id}/`;
  } else {
    url = `salon/${salonId}/client/`;
  }
  if (!method || method === 'GET') {
    return {
      [AUTH_API]: {
        api_root: CONF.apiRoot,
        endpoint: url,
        authenticated: true,
        types: [CHECKOUT_CLIENT_REQUEST, CHECKOUT_CLIENT_SUCCESS, CHECKOUT_CLIENT_FAILURE],
        params: { salonId, data, method: 'GET' },
        config: {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      },
    };
  }
  return {
    [AUTH_API]: {
      api_root: CONF.apiRoot,
      endpoint: url,
      authenticated: true,
      types: [CHECKOUT_CLIENT_REQUEST, CHECKOUT_CLIENT_SUCCESS, CHECKOUT_CLIENT_FAILURE],
      params: { salonId, data, method },
      config: {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    },
  };
}

export function fetchDeposit(salonId, appointmentId) {
  return {
    [AUTH_API]: {
      api_root: CONF.transactionApiRoot,
      endpoint: `deposit/${salonId}/appointment/${appointmentId}/`,
      authenticated: true,
      types: [CHECKOUT_FETCH_DEPOSIT_REQUEST, CHECKOUT_FETCH_DEPOSIT_SUCCESS, CHECKOUT_FETCH_DEPOSIT_FAILURE],
      config: {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export const setInitalDepositSelection = (salonId, appointmentId) => ({
  [AUTH_API]: {
    api_root: CONF.transactionApiRoot,
    endpoint: `deposit/${salonId}/appointment/${appointmentId}/`,
    authenticated: true,
    types: [CHECKOUT_INIT_DEPOSIT_REQUEST, CHECKOUT_INIT_DEPOSIT_SUCCESS, CHECKOUT_INIT_DEPOSIT_FAILURE],
    params: { appointmentId },
    config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  },
});

export function setCheckoutPayButtons(enabled) {
  return {
    type: TOGGLE_CHECKOUT_BUTTONS_ENABLED,
    enabled,
  };
}

export function startCheckoutSession(salonId, defaultStylistId) {
  return {
    [AUTH_API]: {
      api_root: CONF.stockApiRoot,
      endpoint: `sessions/salon/${salonId}/checkout/start/`,
      authenticated: true, // Default failure handling is fine here
      types: [CHECKOUT_START_SESSION_REQUEST, CHECKOUT_START_SESSION_SUCCESS],
      params: { defaultStylistId },
      config: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export function closeCheckoutSession(salonId) {
  return {
    [AUTH_API]: {
      api_root: CONF.stockApiRoot,
      endpoint: `sessions/salon/${salonId}/checkout/close/`,
      authenticated: true, // Default failure handling is fine here
      types: [CHECKOUT_CLOSE_SESSION_REQUEST, CHECKOUT_CLOSE_SESSION_SUCCESS, CHECKOUT_CLOSE_SESSION_FAILURE],
      config: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export const updateCheckoutData = (isClientCard, cashValue, otherValue, chargecardOnFile) => ({
  type: UPDATE_CHECKOUT_COMPLETE,
  isClientCard,
  cashValue,
  otherValue,
  chargecardOnFile,
});

export function fetchAllDepositClientsList(salonId) {
  return {
    [AUTH_API]: {
      api_root: CONF.transactionApiRoot,
      endpoint: `deposit/${salonId}/clients/`,
      authenticated: true,
      types: [
        CHECKOUT_FETCH_ALL_DEPOSITS_REQUEST,
        CHECKOUT_FETCH_ALL_DEPOSITS_SUCCESS,
        CHECKOUT_FETCH_ALL_DEPOSITS_FAILURE,
      ],
      config: {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  };
}

export const fetchNextAppointmentDetails = (salonId, clientId, appointmentId) => ({
  [AUTH_API]: {
    api_root: CONF.apiRoot,
    endpoint: `salon/${salonId}/next/appointment/${clientId}/${appointmentId}/`,
    authenticated: true,
    types: [FETCH_NEXT_APPOINTMENT_REQUEST, FETCH_NEXT_APPOINTMENT_SUCCESS, FETCH_NEXT_APPOINTMENT_FAILURE],
    params: { clientId },
    config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  },
});

export const exportTransactionLog = (salonId, startDate, endDate, tz) => {
  const bgn = fromAnyDateToZuluDayBgn(startDate);
  const end = fromAnyDateToZuluDayEnd(endDate);
  return {
    [AUTH_API]: {
      api_root: CONF.transactionApiRoot,
      endpoint: `checkout/${salonId}/transactions/export/?start=${bgn}&end=${end}&time_zone=${tz}`,
      authenticated: true,
      types: [EXPORT_TRANSACTION_REQUEST, EXPORT_TRANSACTION_SUCCESS, EXPORT_TRANSACTION_FAILURE],
      config: { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    },
  };
};

export const clearTransactionLog = () => ({ type: CLEAR_TRANSACTION_LOG });
export const cancelChargeSlickCardOnline = () => ({ type: CANCEL_SLICK_CARD_ONLINE });
export const updateVouchersUsed = (vouchersUsed) => ({ type: UPDATE_VOUCHERS_USED, vouchers_used: vouchersUsed });
